import React, { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  ColunaOrdenavelConjunto,
  ConjuntoAgregado,
  DirecaoOrdenacao,
  OrdenacaoConjuntos,
} from 'helper/windx';
import LinhaConjunto from '../LinhaConjunto';

interface TabelaConjuntosProps {
  conjuntos: ConjuntoAgregado[];
  isSelecionado: (id: number) => boolean;
  onToggle: (id: number) => void;
  onSelecionarVisiveis: (ids: number[]) => void;
  onDesselecionarVisiveis: (ids: number[]) => void;
  ordenacao: OrdenacaoConjuntos;
  onOrdenacaoChange: (o: OrdenacaoConjuntos) => void;
}

const ALTURA_LINHA = 36;
const OVERSCAN = 8;

interface ColunaDef {
  chave: ColunaOrdenavelConjunto;
  label: string;
  className: string;
}

// WINDX-024: header "NOME" virou "PONTO DE CONEXAO" (semantica corrigida —
// sempre foi o nome da subestacao). Coluna nova "LABEL" exibe o identificador
// desambiguado `{cod_conjunto}_{UPPER(ponto_de_conexao)}` do BD.
const COLUNAS: ColunaDef[] = [
  { chave: 'cod_conjunto', label: 'CÓDIGO', className: 'windx-seletor-conjuntos__cell--codigo' },
  { chave: 'ponto_de_conexao', label: 'PONTO DE CONEXÃO', className: 'windx-seletor-conjuntos__cell--nome' },
  { chave: 'label', label: 'LABEL', className: 'windx-seletor-conjuntos__cell--label' },
  { chave: 'uf_sigla', label: 'UF', className: 'windx-seletor-conjuntos__cell--uf' },
  { chave: 'subsistema_sm_sigla', label: 'SM', className: 'windx-seletor-conjuntos__cell--sub-sm' },
  { chave: 'subsistema_sh_sigla', label: 'SH', className: 'windx-seletor-conjuntos__cell--sub-sh' },
  { chave: 'localizacao_epe', label: 'EPE', className: 'windx-seletor-conjuntos__cell--epe' },
  { chave: 'qtd_usinas', label: '# USINAS', className: 'windx-seletor-conjuntos__cell--qtd' },
  { chave: 'potencia_total_mw', label: 'MW TOTAL', className: 'windx-seletor-conjuntos__cell--mw' },
];

function proximaDirecao(atual: DirecaoOrdenacao): DirecaoOrdenacao {
  return atual === 'asc' ? 'desc' : 'asc';
}

function ariaSort(
  ordenacao: OrdenacaoConjuntos,
  chave: ColunaOrdenavelConjunto,
): 'ascending' | 'descending' | 'none' {
  if (ordenacao.coluna !== chave) return 'none';
  return ordenacao.direcao === 'asc' ? 'ascending' : 'descending';
}

/**
 * Tabela virtualizada de conjuntos eolicos.
 *
 * Mesma engine do `TabelaUsinas`: ~20 linhas no DOM via `@tanstack/
 * react-virtual`, container com altura fixa 480px, header pinned com
 * checkbox tri-state. Ver `TabelaUsinas/index.tsx` para detalhes do
 * `indeterminate` via DOM ref (React nao expoe prop).
 *
 * 8 colunas ordenaveis (vs 6 na de usinas) — `qtd_usinas` e
 * `potencia_total_mw` sao numericas (alinhadas a direita), demais sao
 * strings ordenadas por `localeCompare('pt-BR')` no helper.
 */
export default function TabelaConjuntos({
  conjuntos,
  isSelecionado,
  onToggle,
  onSelecionarVisiveis,
  onDesselecionarVisiveis,
  ordenacao,
  onOrdenacaoChange,
}: TabelaConjuntosProps) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const checkboxTodosRef = useRef<HTMLInputElement | null>(null);

  const virtualizer = useVirtualizer({
    count: conjuntos.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ALTURA_LINHA,
    overscan: OVERSCAN,
  });

  const idsVisiveis = conjuntos.map((c) => c.id);
  const selecionadosVisiveis = idsVisiveis.filter(isSelecionado).length;
  const todosVisiveisSelecionados = (
    idsVisiveis.length > 0 && selecionadosVisiveis === idsVisiveis.length
  );
  const parcialVisivel = (
    selecionadosVisiveis > 0 && selecionadosVisiveis < idsVisiveis.length
  );

  useEffect(() => {
    if (checkboxTodosRef.current) {
      checkboxTodosRef.current.indeterminate = parcialVisivel;
    }
  }, [parcialVisivel]);

  const onCheckTodos = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onSelecionarVisiveis(idsVisiveis);
    } else {
      onDesselecionarVisiveis(idsVisiveis);
    }
  };

  const handleOrdenar = (chave: ColunaOrdenavelConjunto) => {
    if (ordenacao.coluna === chave) {
      onOrdenacaoChange({ coluna: chave, direcao: proximaDirecao(ordenacao.direcao) });
    } else {
      onOrdenacaoChange({ coluna: chave, direcao: 'asc' });
    }
  };

  const setaOrdenacao = (chave: ColunaOrdenavelConjunto) => {
    if (ordenacao.coluna !== chave) return '';
    return ordenacao.direcao === 'asc' ? ' ↑' : ' ↓';
  };

  const totalSize = virtualizer.getTotalSize();
  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div className="windx-seletor-conjuntos__tabela" role="table" aria-rowcount={conjuntos.length}>
      <div className="windx-seletor-conjuntos__tabela-head" role="row">
        <div className="windx-seletor-conjuntos__cell windx-seletor-conjuntos__cell--check" role="columnheader">
          <input
            ref={checkboxTodosRef}
            type="checkbox"
            className="form-check-input"
            checked={todosVisiveisSelecionados}
            onChange={onCheckTodos}
            disabled={idsVisiveis.length === 0}
            aria-label="Selecionar todos os visíveis"
          />
        </div>
        {COLUNAS.map((col) => (
          <button
            key={col.chave}
            type="button"
            role="columnheader"
            aria-sort={ariaSort(ordenacao, col.chave)}
            className={`windx-seletor-conjuntos__cell ${col.className} windx-seletor-conjuntos__th`}
            onClick={() => handleOrdenar(col.chave)}
          >
            {col.label}
            {setaOrdenacao(col.chave)}
          </button>
        ))}
      </div>
      <div ref={scrollRef} className="windx-seletor-conjuntos__tabela-body">
        {conjuntos.length === 0 ? (
          <div className="windx-seletor-conjuntos__vazio">
            Nenhum conjunto encontrado para os filtros aplicados.
          </div>
        ) : (
          <div style={{ height: `${totalSize}px`, position: 'relative' }}>
            {virtualItems.map((item) => {
              const conjunto = conjuntos[item.index];
              return (
                <LinhaConjunto
                  key={conjunto.id}
                  conjunto={conjunto}
                  selecionado={isSelecionado(conjunto.id)}
                  onToggle={onToggle}
                  altura={ALTURA_LINHA}
                  translateY={item.start}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
